import { FC, useContext, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/pro-duotone-svg-icons';
import { Button } from '@mui/material';
import MapContext, { MapFilterPeriodEnum } from '../../../contexts/data/MapContext';
import dayjs from 'dayjs';
import useGetCommunityGroup from '../../../hooks/data/useGetCommunityGroup';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const getImageAsync = (src?: string): Promise<HTMLImageElement> => {
    const imgPromise = new Promise<HTMLImageElement>((res, rej) => {
        if (!src) {
            rej("No Source Provided");
            return;
        }

        const img = document.createElement("img");
    
        img.onload = () => {
            res(img);
        }

        img.src = src;
    });

    return imgPromise;
}

interface DownloadGroupReportProps {
    activeGroupMembers: number | undefined,
    groupChartMemberNames: string[] | undefined,
}

const DownloadGroupReport: FC<DownloadGroupReportProps> = ({
    activeGroupMembers,
    groupChartMemberNames
}) => {
    //
    // -- Contexts --
    const { filter } = useContext(MapContext);

    //
    // -- Params --
    const { species: speciesName } = useParams();

    //
    // -- States --
    const [creatingPDF, setCreatingPDF] = useState<boolean>(false);
    
    const { data: communityGroupData } =  useGetCommunityGroup(
        filter?.bySubmitter?.communityGroupId 
            ? {communityGroupId: filter?.bySubmitter?.communityGroupId} 
            : undefined,
        { revalidateOnFocus: false, errorRetryCount: 0 }
    );

    const getReportPeriodFormatted = () => {
        // Period
        let from = null;
        let to = null;
        switch (filter.reportingPeriod.period) {
            case MapFilterPeriodEnum.DateRange: 
                from = filter.reportingPeriod.fromField;
                to = filter.reportingPeriod.toField;
                break;

            case MapFilterPeriodEnum.Year: 
                const year = filter.reportingPeriod.yearField;
                from = year?.startOf('year');

                // If the year is equal to this year, we want to cap off
                // at the current date.
                if (year?.year() === dayjs().year()) {
                    to = dayjs();
                } else {
                    to = year?.endOf('year');
                }

                break;
                
            case MapFilterPeriodEnum.MonthYear: 
                const monthYear = filter.reportingPeriod.monthYearField;
                from = monthYear?.startOf('month');

                // If the year is equal to this year, we want to cap off
                // at the current date.
                if (monthYear?.year() === dayjs().year() && monthYear?.month() === dayjs().month()) {
                    to = dayjs();
                } else {
                    to = monthYear?.endOf('month');
                }

                break;

            case MapFilterPeriodEnum.LastSevenDays: 
                from = dayjs().subtract(7, 'days');  
                to = dayjs();  
                break;

            case MapFilterPeriodEnum.LastThirtyOneDays: 
                from = dayjs().subtract(31, 'days');  
                to = dayjs();  
                break;
        }

        return `${from?.format("DD MMM YYYY")} to ${to?.format("DD MMM YYYY")}`;
    }

    const handleDownloadPDF = async () => {
        if (creatingPDF) return;

        setCreatingPDF(true);

        // Html elements for document
        const logoSource = document.querySelector<HTMLImageElement>('#header-logo img')?.src;
        const headerLogo = await getImageAsync(logoSource);
        const elements = {
            sightingTable: document.getElementById('sighting-table'),
            sightingGraph: document.getElementById('sighting-graph'),
            damageTable: document.getElementById('damage-table'),
            damageGraph: document.getElementById('damage-graph'),
            controlTable: document.getElementById('control-table'),
            controlGraph: document.getElementById('control-graph')
        };

        // Add page content and elements
        const sections = [
            { table: elements.sightingTable, graph: elements.sightingGraph },
            { table: elements.damageTable, graph: elements.damageGraph },
            { table: elements.controlTable, graph: elements.controlGraph }
        ];

        if (!headerLogo) {
            setCreatingPDF(false);
            return;
        }
        
        // Document variables
        const pdf = new jsPDF({orientation: 'landscape'});
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const generatedDateTxt = `Generated: ${dayjs(new Date()).format("DD/MM/YYYY h:mm A")}`;
        const tempMargin = 5;
        const titleTxtSize = 16;
        const bodyTxtSize = 10;
        const footerTxtSize = 8;
        let currentPageDistanceY = tempMargin;

        // Create header logo
        const logoCanvas: HTMLCanvasElement = document.createElement('canvas');
        logoCanvas.getContext('2d')?.drawImage(headerLogo, 0, 0);
        const bannerImg = logoCanvas.toDataURL('image/png');
        const bannerImgHeight = headerLogo.naturalHeight;
        const bannerImgWidth = headerLogo.naturalWidth;
        const bannerImgRatio = bannerImgWidth / bannerImgHeight;
        const scaledBannerImgWidth = pageWidth / 5;
        const scaledBannerImgHeight = scaledBannerImgWidth / bannerImgRatio;
        
        for (let i = 0; i < sections.length; i++) {
            if (i > 0) {
                pdf.addPage();
                currentPageDistanceY = 0;
            }
    
            const { table, graph } = sections[i];

            // Add header logo
            currentPageDistanceY += scaledBannerImgHeight;
            pdf.addImage(bannerImg, 'PNG', tempMargin, tempMargin, scaledBannerImgWidth, scaledBannerImgHeight);
    
            // Add horzontal line
            pdf.setDrawColor(0, 0, 0);
            pdf.line(tempMargin, scaledBannerImgHeight, pageWidth - tempMargin, scaledBannerImgHeight, 'S');

            // Create title
            const title = `${communityGroupData?.name}: ${speciesName?.replace("scan", "")?.replace("alert", "")?.capitaliseFirstLetter() ?? ""} Report`;
            if (i === 0){
                // Add title
                pdf.setFontSize(titleTxtSize);
                pdf.setFont('helvetica', 'bold');
                pdf.text(title, pageWidth - tempMargin, tempMargin * 2, { align: 'right' });
        
                // Add group and filter information
                pdf.setFontSize(bodyTxtSize);
                pdf.setFont('helvetica', 'normal');
                const reportPeriodText = `Report period: ${getReportPeriodFormatted()}. Number of active group members this period: ${activeGroupMembers}`;
                pdf.text(reportPeriodText, tempMargin, currentPageDistanceY);
                currentPageDistanceY += tempMargin * 2;
        
                pdf.text(`Group Members: ${groupChartMemberNames?.join(', ')}`, tempMargin, currentPageDistanceY, { maxWidth: pageWidth - tempMargin * 2 });
                currentPageDistanceY += tempMargin;
            }

            // Add table to document
            if (table){
                await html2canvas(table).then((canvas) => {
                    const imgData = canvas.toDataURL('image/png');
                    const pageWidth = pdf.internal.pageSize.getWidth();
                
                    const tblImageRatio = table?.offsetWidth / table?.offsetHeight;
                    const scaledTblImageWidth = ((pageWidth - (tempMargin * 3)) / 2.2);
                    const scaledTblImageHeight = scaledTblImageWidth / tblImageRatio;
                
                    pdf.addImage(imgData, 'PNG', tempMargin, currentPageDistanceY + 5, scaledTblImageWidth, scaledTblImageHeight);
                });
            }

            // Add graph to document
            if (graph){
                await html2canvas(graph).then((canvas) => {
                    const imgData = canvas.toDataURL('image/png');
                    const pageWidth = pdf.internal.pageSize.getWidth();
                
                    const tblImageRatio = graph?.offsetWidth / graph?.offsetHeight;
                    const scaledTblImageWidth = ((pageWidth - (tempMargin * 3)) / 2);
                    const scaledTblImageHeight = scaledTblImageWidth / tblImageRatio;
                
                    pdf.addImage(imgData, 'PNG', pageWidth - tempMargin - scaledTblImageWidth, currentPageDistanceY + 5, scaledTblImageWidth, scaledTblImageHeight);
                });
            }

            // Add footer
            var footerDistanceY = pageHeight - (tempMargin / 2);
            pdf.setFontSize(footerTxtSize);
            pdf.text(generatedDateTxt, tempMargin, footerDistanceY, {
                align: 'left',
            });
            pdf.text(title, (pageWidth / 2), footerDistanceY, {
                align: 'center',
            });
            pdf.text(`Page ${i+1} of 3`, pageWidth - tempMargin, footerDistanceY, {
                align: 'right',
            });
            
            // Add horzontal line
            pdf.setDrawColor(0, 0, 0);
            pdf.line(tempMargin, footerDistanceY - 5, pageWidth - tempMargin, footerDistanceY - 5, 'S');
        }

        pdf.save(`${communityGroupData?.name} ${speciesName?.replace("scan", "")?.replace("alert", "")?.capitaliseFirstLetter() ?? ""} Report.pdf`); 
        setCreatingPDF(false);
    };

    return (
        <Button
            variant="text"
            sx={{
                color: 'white',
                fontSize: '0.65rem',
                '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.15)'
                }
            }}
            startIcon={
                <FontAwesomeIcon
                    icon={faDownload} 
                    style={{ 
                        color: 'white',
                        fontSize: '0.65rem',
                        paddingRight: '3px' 
                    }}
                />
            }
            onClick={handleDownloadPDF}
        >
            {
                creatingPDF ? "Downloading..." : "Download Group Report (PDF)"
            }
        </Button>
    );
};

export default DownloadGroupReport;
